
import React, {
  useMemo,
  useState,
} from "react";

import LineIcon from "./LineIcon";


/* =========================================================
   DATE HELPER
========================================================= */

const formatDateKey = (dateValue) => {

  if (!dateValue) {
    return "";
  }

  if (
    typeof dateValue === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(
      dateValue
    )
  ) {
    return dateValue;
  }

  const date =
    new Date(
      dateValue
    );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }

  const year =
    date.getFullYear();

  const month =
    String(
      date.getMonth() + 1
    ).padStart(2, "0");

  const day =
    String(
      date.getDate()
    ).padStart(2, "0");

  return `${year}-${month}-${day}`;

};


/* =========================================================
   GET STAY DATES
========================================================= */

const getStayDates = (
  checkIn,
  checkOut
) => {

  const dates = [];

  if (
    !checkIn ||
    !checkOut
  ) {
    return dates;
  }

  const checkInDate =
    formatDateKey(
      checkIn
    );

  const checkOutDate =
    formatDateKey(
      checkOut
    );

  if (
    !checkInDate ||
    !checkOutDate
  ) {
    return dates;
  }

  const start =
    new Date(
      `${checkInDate}T00:00:00`
    );

  const end =
    new Date(
      `${checkOutDate}T00:00:00`
    );

  if (
    Number.isNaN(
      start.getTime()
    ) ||
    Number.isNaN(
      end.getTime()
    ) ||
    start >= end
  ) {
    return dates;
  }

  const current =
    new Date(
      start
    );

  while (
    current < end
  ) {

    dates.push(
      formatDateKey(
        current
      )
    );

    current.setDate(
      current.getDate() + 1
    );

  }

  return dates;

};


/* =========================================================
   DISPLAY DATE
========================================================= */

const formatDisplayDate = (
  dateValue
) => {

  if (!dateValue) {
    return "";
  }

  const date =
    new Date(
      `${formatDateKey(
        dateValue
      )}T00:00:00`
    );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return dateValue;
  }

  return date.toLocaleDateString(
    "en-IN",
    {
      day: "numeric",
      month: "short",
      year: "numeric",
    }
  );

};


/* =========================================================
   CHECK COUPON / OFFER VALIDITY
========================================================= */

const isOfferValid = (
  offer,
  roomAmount
) => {

  if (!offer) {
    return false;
  }

  if (
    offer.isActive === false
  ) {
    return false;
  }

  /*
   * Only reservation offers/coupons
   */

  if (
    offer.offerType &&
    offer.offerType !==
      "RESERVATION"
  ) {
    return false;
  }

  /*
   * VALID FROM
   */

  const today =
    new Date();

  today.setHours(
    0,
    0,
    0,
    0
  );

  if (
    offer.validFrom
  ) {

    const validFrom =
      new Date(
        offer.validFrom
      );

    validFrom.setHours(
      0,
      0,
      0,
      0
    );

    if (
      today < validFrom
    ) {
      return false;
    }

  }

  /*
   * VALID TO
   */

  if (
    offer.validTo
  ) {

    const validTo =
      new Date(
        offer.validTo
      );

    validTo.setHours(
      23,
      59,
      59,
      999
    );

    if (
      today > validTo
    ) {
      return false;
    }

  }

  /*
   * MINIMUM BOOKING AMOUNT
   */

  const minimumBookingAmount =
    Number(
      offer.minimumBookingAmount ||
      0
    );

  if (
    Number(roomAmount || 0) <
    minimumBookingAmount
  ) {
    return false;
  }

  /*
   * USAGE LIMIT
   */

  if (
    offer.usageLimit !== null &&
    offer.usageLimit !== undefined &&
    Number(
      offer.usedCount || 0
    ) >=
    Number(
      offer.usageLimit
    )
  ) {
    return false;
  }

  return true;

};


/* =========================================================
   CALCULATE COUPON DISCOUNT
========================================================= */

const calculateOfferDiscount = (
  offer,
  amount
) => {

  if (!offer) {
    return 0;
  }

  const bookingAmount =
    Number(
      amount || 0
    );

  if (
    bookingAmount <= 0
  ) {
    return 0;
  }

  let discount = 0;

  /*
   * PERCENTAGE
   */

  if (
    offer.discountType ===
    "PERCENTAGE"
  ) {

    discount =
      (
        bookingAmount *
        Number(
          offer.discountValue || 0
        )
      ) / 100;

    /*
     * MAX DISCOUNT
     */

    if (
      offer.maxDiscountAmount !==
        null &&
      offer.maxDiscountAmount !==
        undefined &&
      Number(
        offer.maxDiscountAmount
      ) > 0 &&
      discount >
        Number(
          offer.maxDiscountAmount
        )
    ) {

      discount =
        Number(
          offer.maxDiscountAmount
        );

    }

  }

  /*
   * FIXED
   */

  else if (
    offer.discountType ===
    "FIXED"
  ) {

    discount =
      Number(
        offer.discountValue || 0
      );

  }

  /*
   * SAFETY
   */

  return Math.max(
    0,
    Math.min(
      discount,
      bookingAmount
    )
  );

};


/* =========================================================
   REVIEW PAGE
========================================================= */

const ReviewPage = ({
  selectedRoom,
  bookingDetails,
  guestForm,
  adults,
  children,
  roomPrice,
  calculatedNights,
  roomTotal,
  taxRate,
  taxAmount,
  finalTotal,
  bookingLoading,
  setBookingStep,
  setCreatedBooking,
  onSubmit,

  /*
   * COUPONS / OFFERS
   */

  offers = [],

}) => {

  /* =======================================================
     SELECTED COUPON
  ======================================================= */

  const [
    selectedOfferId,
    setSelectedOfferId
  ] =
    useState("");


  /* =======================================================
     STAY DATES
  ======================================================= */

  const stayDates =
    useMemo(
      () =>
        getStayDates(
          bookingDetails?.checkIn,
          bookingDetails?.checkOut
        ),
      [
        bookingDetails?.checkIn,
        bookingDetails?.checkOut,
      ]
    );


  /* =======================================================
     NIGHTLY RATE MAP
  ======================================================= */

  const nightlyRateMap =
    useMemo(
      () => {

        if (
          !Array.isArray(
            selectedRoom?.nightlyRates
          )
        ) {
          return {};
        }

        return selectedRoom
          .nightlyRates
          .reduce(
            (
              map,
              item
            ) => {

              const date =
                formatDateKey(
                  item?.date
                );

              const rate =
                Number(
                  item?.rate ??
                  item?.newRate ??
                  item?.price ??
                  0
                );

              if (
                date &&
                Number.isFinite(
                  rate
                )
              ) {

                map[date] =
                  rate;

              }

              return map;

            },
            {}
          );

      },
      [
        selectedRoom?.nightlyRates
      ]
    );


  /* =======================================================
     FALLBACK ROOM PRICE
  ======================================================= */

  const fallbackRoomPrice =
    Number(
      selectedRoom?.channelManagerRate ??
      selectedRoom?.todayPrice ??
      selectedRoom?.price ??
      selectedRoom?.basePrice ??
      roomPrice ??
      0
    );


  /* =======================================================
     NIGHTLY BREAKDOWN
  ======================================================= */

  const nightlyBreakdown =
    useMemo(
      () =>
        stayDates.map(
          (date) => {

            const rate =
              Number(
                nightlyRateMap[
                  date
                ] ??
                fallbackRoomPrice
              );

            return {

              date,

              rate,

            };

          }
        ),
      [
        stayDates,
        nightlyRateMap,
        fallbackRoomPrice,
      ]
    );


  /* =======================================================
     ACTUAL ROOM TOTAL
  ======================================================= */

  const actualRoomTotal =
    nightlyBreakdown.length > 0
      ? nightlyBreakdown.reduce(
          (
            total,
            night
          ) =>
            total +
            Number(
              night.rate || 0
            ),
          0
        )
      : Number(
          roomTotal || 0
        );


  /* =======================================================
     VALID AVAILABLE COUPONS
  ======================================================= */

  const availableOffers =
    useMemo(
      () => {

        if (
          !Array.isArray(
            offers
          )
        ) {
          return [];
        }

        return offers.filter(
          (offer) =>
            isOfferValid(
              offer,
              actualRoomTotal
            )
        );

      },
      [
        offers,
        actualRoomTotal,
      ]
    );


  /* =======================================================
     SELECTED COUPON
  ======================================================= */

  const selectedOffer =
    availableOffers.find(
      (offer) =>
        String(
          offer._id
        ) ===
        String(
          selectedOfferId
        )
    ) ||
    null;


  /* =======================================================
     COUPON DISCOUNT
  ======================================================= */

  const offerDiscountAmount =
    calculateOfferDiscount(
      selectedOffer,
      actualRoomTotal
    );


  /* =======================================================
     ROOM TOTAL AFTER COUPON
  ======================================================= */

  const roomTotalAfterOffer =
    Math.max(
      actualRoomTotal -
      offerDiscountAmount,
      0
    );


  /* =======================================================
     TAX AFTER COUPON
  ======================================================= */

  const actualTaxAmount =
    taxRate !== undefined &&
    taxRate !== null
      ? (
          roomTotalAfterOffer *
          Number(
            taxRate || 0
          )
        ) / 100
      : Number(
          taxAmount || 0
        );


  /* =======================================================
     FINAL TOTAL
  ======================================================= */

  const actualFinalTotal =
    roomTotalAfterOffer +
    actualTaxAmount;


  /* =======================================================
     COUPON CHANGE
  ======================================================= */

  const handleCouponChange =
    (event) => {

      const couponId =
        event.target.value;

      console.log(
        "🎟️ COUPON SELECTED:",
        couponId
      );

      setSelectedOfferId(
        couponId
      );

    };


  /* =======================================================
     CLEAR COUPON
  ======================================================= */

  const handleRemoveCoupon =
    () => {

      console.log(
        "🎟️ COUPON REMOVED"
      );

      setSelectedOfferId(
        ""
      );

    };


  /* =======================================================
     DEBUG
  ======================================================= */

  console.log(
    "🎟️ AVAILABLE COUPONS:",
    availableOffers
  );

  console.log(
    "🎟️ SELECTED COUPON:",
    selectedOffer
  );

  console.log(
    "💰 REVIEW TOTALS:",
    {
      actualRoomTotal,
      offerDiscountAmount,
      roomTotalAfterOffer,
      actualTaxAmount,
      actualFinalTotal,
    }
  );


  return (

    <div className="be-booking-review-page">

      <div className="be-review-layout">

        {/* ===================================================
            LEFT — ROOM
        =================================================== */}

        <section className="be-review-room-column">

          <div className="be-review-room-card">

            <div className="be-review-room-image">

              <img
                src={
                  selectedRoom.image ||
                  selectedRoom.images?.[0]
                }
                alt={
                  selectedRoom.name
                }
              />

            </div>


            <div className="be-review-room-content">

              <span className="be-room-type">
                {selectedRoom.type}
              </span>

              <h2>
                {selectedRoom.name}
              </h2>


              <div className="be-review-room-meta">

                <span>

                  <LineIcon
                    type="bed"
                    size={18}
                  />

                  {
                    selectedRoom.bedType ||
                    "Bed available"
                  }

                </span>


                <span>

                  <LineIcon
                    type="users"
                    size={18}
                  />

                  {
                    selectedRoom.guests ||
                    selectedRoom.maxAdults ||
                    1
                  }{" "}
                  guests

                </span>


                {selectedRoom.bathrooms > 0 && (

                  <span>

                    <LineIcon
                      type="bath"
                      size={18}
                    />

                    {
                      selectedRoom.bathrooms
                    }{" "}
                    bathroom
                    {
                      selectedRoom.bathrooms > 1
                        ? "s"
                        : ""
                    }

                  </span>

                )}

              </div>


              {selectedRoom.description && (

                <p className="be-review-room-description">

                  {
                    selectedRoom.description
                  }

                </p>

              )}


              {
                selectedRoom.amenities?.length > 0 && (

                  <div className="be-review-room-amenities">

                    {
                      selectedRoom.amenities
                        .slice(
                          0,
                          6
                        )
                        .map(
                          (amenity) => (

                            <span
                              key={amenity}
                            >

                              <LineIcon
                                type="check"
                                size={14}
                              />

                              {amenity}

                            </span>

                          )
                        )
                    }

                  </div>

                )
              }

            </div>


            <div className="be-review-room-price">

              <span>
                Room price
              </span>

              <strong>

                ₹
                {
                  actualRoomTotal
                    .toLocaleString(
                      "en-IN"
                    )
                }

              </strong>

              <small>

                {
                  calculatedNights
                }{" "}

                {
                  calculatedNights === 1
                    ? "night"
                    : "nights"
                }

              </small>

            </div>

          </div>


          {/* =================================================
              DATE-WISE RATE BREAKDOWN
          ================================================= */}

          {
            nightlyBreakdown.length > 0 && (

              <div className="be-review-info-card">

                <div className="be-review-info-header">

                  <div>

                    <span className="be-card-step">
                      ₹
                    </span>

                    <div>

                      <h3>
                        Nightly rate breakdown
                      </h3>

                      <p>
                        Room charges for each night.
                      </p>

                    </div>

                  </div>

                </div>


                <div className="be-nightly-rate-list">

                  {
                    nightlyBreakdown.map(
                      (night) => (

                        <div
                          className="be-payment-row"
                          key={night.date}
                        >

                          <span>

                            {
                              formatDisplayDate(
                                night.date
                              )
                            }

                          </span>

                          <strong>

                            ₹
                            {
                              Number(
                                night.rate
                              ).toLocaleString(
                                "en-IN"
                              )
                            }

                          </strong>

                        </div>

                      )
                    )
                  }

                </div>

              </div>

            )
          }

        </section>


        {/* ===================================================
            RIGHT — BOOKING INFORMATION
        =================================================== */}

        <section className="be-review-details-column">


          {/* =================================================
              STAY DETAILS
          ================================================= */}

          <div className="be-review-info-card">

            <div className="be-review-info-header">

              <div>

                <span className="be-card-step">
                  01
                </span>

                <div>

                  <h3>
                    Stay details
                  </h3>

                  <p>
                    Your selected dates and guests.
                  </p>

                </div>

              </div>

            </div>


            <div className="be-review-form-grid">

              <div className="be-review-form-group">

                <label>
                  Check-in
                </label>

                <div className="be-review-readonly-field">

                  <LineIcon
                    type="calendar"
                    size={17}
                  />

                  <input
                    type="text"
                    value={
                      bookingDetails.checkIn ||
                      ""
                    }
                    readOnly
                  />

                </div>

              </div>


              <div className="be-review-form-group">

                <label>
                  Check-out
                </label>

                <div className="be-review-readonly-field">

                  <LineIcon
                    type="calendar"
                    size={17}
                  />

                  <input
                    type="text"
                    value={
                      bookingDetails.checkOut ||
                      ""
                    }
                    readOnly
                  />

                </div>

              </div>


              <div className="be-review-form-group">

                <label>
                  Nights
                </label>

                <div className="be-review-readonly-field">

                  <LineIcon
                    type="calendar"
                    size={17}
                  />

                  <input
                    type="text"
                    value={
                      `${calculatedNights} ${
                        calculatedNights === 1
                          ? "Night"
                          : "Nights"
                      }`
                    }
                    readOnly
                  />

                </div>

              </div>


              <div className="be-review-form-group">

                <label>
                  Guests
                </label>

                <div className="be-review-readonly-field">

                  <LineIcon
                    type="users"
                    size={17}
                  />

                  <input
                    type="text"
                    value={
                      `${adults} ${
                        adults === 1
                          ? "Adult"
                          : "Adults"
                      }${
                        children > 0
                          ? ` · ${children} ${
                              children === 1
                                ? "Child"
                                : "Children"
                            }`
                          : ""
                      }`
                    }
                    readOnly
                  />

                </div>

              </div>

            </div>

          </div>


          {/* =================================================
              GUEST DETAILS
          ================================================= */}

          <div className="be-review-info-card">

            <div className="be-review-info-header">

              <div>

                <span className="be-card-step">
                  02
                </span>

                <div>

                  <h3>
                    Guest details
                  </h3>

                  <p>
                    Contact information for this reservation.
                  </p>

                </div>

              </div>

            </div>


            <div className="be-review-form-grid">

              <div className="be-review-form-group">

                <label>
                  Full name
                </label>

                <input
                  type="text"
                  value={
                    guestForm.name ||
                    ""
                  }
                  readOnly
                />

              </div>


              <div className="be-review-form-group">

                <label>
                  Mobile number
                </label>

                <div className="be-review-readonly-field">

                  <LineIcon
                    type="phone"
                    size={17}
                  />

                  <input
                    type="text"
                    value={
                      guestForm.mobile ||
                      ""
                    }
                    readOnly
                  />

                </div>

              </div>


              <div className="be-review-form-group be-review-full-width">

                <label>
                  Email address
                </label>

                <div className="be-review-readonly-field">

                  <LineIcon
                    type="mail"
                    size={17}
                  />

                  <input
                    type="text"
                    value={
                      guestForm.email ||
                      ""
                    }
                    readOnly
                  />

                </div>

              </div>

            </div>

          </div>


          {/* =================================================
              SPECIAL REQUEST
          ================================================= */}

          <div className="be-review-info-card">

            <div className="be-review-info-header">

              <div>

                <span className="be-card-step">
                  03
                </span>

                <div>

                  <h3>
                    Special request
                  </h3>

                  <p>
                    Additional information provided by you.
                  </p>

                </div>

              </div>

            </div>


            <div className="be-review-special-request">

              <textarea
                value={
                  guestForm.specialRequest ||
                  "No special request added."
                }
                readOnly
                rows={4}
              />

            </div>

          </div>


          {/* =================================================
              COUPON
          ================================================= */}

          <div className="be-review-info-card">

            <div className="be-review-info-header">

              <div>

                <span className="be-card-step">
                  🎟️
                </span>

                <div>

                  <h3>
                    Apply coupon
                  </h3>

                  <p>
                    Select an available coupon to get a discount.
                  </p>

                </div>

              </div>

            </div>


            {
              availableOffers.length > 0 ? (

                <div className="be-review-form-group">

                  <label>
                    Available coupons
                  </label>


                  <select
                    className="offer-input"
                    value={
                      selectedOfferId
                    }
                    onChange={
                      handleCouponChange
                    }
                  >

                    <option value="">
                      No coupon applied
                    </option>


                    {
                      availableOffers.map(
                        (offer) => (

                          <option
                            key={
                              offer._id
                            }
                            value={
                              offer._id
                            }
                          >

                            {
                              offer.code
                                ? `${offer.code} — `
                                : ""
                            }

                            {
                              offer.name
                            }

                            {" — "}

                            {
                              offer.discountType ===
                              "PERCENTAGE"
                                ? `${offer.discountValue}% OFF`
                                : `₹${Number(
                                    offer.discountValue || 0
                                  ).toLocaleString(
                                    "en-IN"
                                  )} OFF`
                            }

                          </option>

                        )
                      )
                    }

                  </select>


                  {/* =================================================
                      SELECTED COUPON DETAILS
                  ================================================= */}

                  {
                    selectedOffer && (

                      <div className="be-applied-offer-info">

                        <div>

                          <strong>
                            {
                              selectedOffer.code ||
                              selectedOffer.name
                            }
                          </strong>

                          {
                            selectedOffer.code &&
                            selectedOffer.name && (

                              <span>
                                {" — "}
                                {
                                  selectedOffer.name
                                }
                              </span>

                            )
                          }

                        </div>


                        {
                          selectedOffer.description && (

                            <p>
                              {
                                selectedOffer.description
                              }
                            </p>

                          )
                        }


                        {
                          selectedOffer.minimumBookingAmount &&
                          Number(
                            selectedOffer.minimumBookingAmount
                          ) > 0 && (

                            <small>

                              Minimum booking:
                              {" ₹"}
                              {
                                Number(
                                  selectedOffer.minimumBookingAmount
                                ).toLocaleString(
                                  "en-IN"
                                )
                              }

                            </small>

                          )
                        }


                        <span>

                          You save ₹
                          {
                            offerDiscountAmount
                              .toLocaleString(
                                "en-IN"
                              )
                          }

                        </span>


                        <button
                          type="button"
                          className="be-remove-coupon-button"
                          onClick={
                            handleRemoveCoupon
                          }
                        >
                          Remove coupon
                        </button>

                      </div>

                    )
                  }

                </div>

              ) : (

                <div className="be-no-coupon-message">

                  No coupons are currently available
                  for this booking.

                </div>

              )
            }

          </div>


          {/* =================================================
              PAYMENT
          ================================================= */}

          <div className="be-review-payment-card">

            <div className="be-review-payment-header">

              <div>

                <span className="be-card-step">
                  04
                </span>

                <div>

                  <h3>
                    Payment
                  </h3>

                  <p>
                    Booking payment summary.
                  </p>

                </div>

              </div>

              <span className="be-payment-status">
                Pay at property
              </span>

            </div>


            <div className="be-review-payment-body">


              {/* ROOM TOTAL */}

              <div className="be-payment-row">

                <span>
                  Room price
                </span>

                <strong>

                  ₹
                  {
                    actualRoomTotal
                      .toLocaleString(
                        "en-IN"
                      )
                  }

                </strong>

              </div>


              {/* NIGHTLY SUMMARY */}

              <div className="be-payment-row">

                <span>
                  Nightly charges
                </span>

                <span className="be-payment-muted">

                  {
                    calculatedNights
                  }{" "}

                  {
                    calculatedNights === 1
                      ? "night"
                      : "nights"
                  }

                </span>

              </div>


              {/* COUPON */}

              {
                selectedOffer && (

                  <div className="be-payment-row">

                    <span>

                      Coupon
                      {" "}
                      (
                      {
                        selectedOffer.code ||
                        selectedOffer.name
                      }
                      )

                    </span>


                    <strong className="be-offer-discount">

                      - ₹
                      {
                        offerDiscountAmount
                          .toLocaleString(
                            "en-IN"
                          )
                      }

                    </strong>

                  </div>

                )
              }


              {/* SUBTOTAL */}

              {
                selectedOffer && (

                  <div className="be-payment-row">

                    <span>
                      Amount after coupon
                    </span>

                    <strong>

                      ₹
                      {
                        roomTotalAfterOffer
                          .toLocaleString(
                            "en-IN"
                          )
                      }

                    </strong>

                  </div>

                )
              }


              {/* TAXES */}

              <div className="be-payment-row">

                <span>
                  Taxes & charges
                </span>

                <strong>

                  ₹
                  {
                    actualTaxAmount
                      .toLocaleString(
                        "en-IN"
                      )
                  }

                </strong>

              </div>


              {/* GST */}

              <div className="be-payment-row">

                <span>
                  GST
                </span>

                <span className="be-payment-muted">

                  {
                    taxRate
                  }%

                </span>

              </div>


              <div className="be-payment-divider" />


              {/* TOTAL */}

              <div className="be-payment-total">

                <span>
                  Total amount
                </span>

                <strong>

                  ₹
                  {
                    actualFinalTotal
                      .toLocaleString(
                        "en-IN"
                      )
                  }

                </strong>

              </div>

            </div>

          </div>


          {/* =================================================
              ACTIONS
          ================================================= */}

          <div className="be-review-actions">


            <button
              type="button"
              className="be-review-back-button"
              disabled={
                bookingLoading
              }
              onClick={() =>
                setBookingStep(
                  "details"
                )
              }
            >

              ← Edit information

            </button>


            <button
              type="button"
              className="be-page-submit-button"
              disabled={
                bookingLoading
              }
              onClick={
                async () => {

                  if (
                    bookingLoading
                  ) {
                    return;
                  }

                  try {

                    /*
                     * Send calculated pricing,
                     * coupon and nightly rates
                     * to the parent.
                     */

                    const bookingResult =
                      await onSubmit({

                        roomTotal:
                          actualRoomTotal,

                        discountAmount:
                          offerDiscountAmount,

                        appliedOffer:
                          selectedOffer,

                        /*
                         * Explicit coupon fields
                         * make backend integration
                         * easier.
                         */

                        couponId:
                          selectedOffer?._id ||
                          null,

                        couponCode:
                          selectedOffer?.code ||
                          null,

                        couponName:
                          selectedOffer?.name ||
                          null,

                        taxAmount:
                          actualTaxAmount,

                        finalTotal:
                          actualFinalTotal,

                        roomTotalAfterDiscount:
                          roomTotalAfterOffer,

                        nightlyRates:
                          nightlyBreakdown,

                      });


                    if (
                      !bookingResult
                    ) {

                      throw new Error(
                        "Booking could not be created."
                      );

                    }


                    setCreatedBooking(
                      bookingResult
                    );


                    setBookingStep(
                      "confirmation"
                    );

                  } catch (
                    error
                  ) {

                    console.error(
                      "Booking confirmation failed:",
                      error
                    );

                  }

                }
              }
            >

              <span>

                {
                  bookingLoading
                    ? "Confirming..."
                    : "Confirm booking"
                }

              </span>


              {
                !bookingLoading && (

                  <span className="be-submit-arrow">

                    <LineIcon
                      type="arrow"
                      size={18}
                    />

                  </span>

                )
              }

            </button>

          </div>


          {/* =================================================
              SECURE NOTE
          ================================================= */}

          <div className="be-review-secure-note">

            <LineIcon
              type="shield"
              size={16}
            />

            <span>
              Your reservation details are securely
              processed and your information is protected.
            </span>

          </div>

        </section>

      </div>

    </div>

  );

};


export default ReviewPage;
